import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cx } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import { useContext, type PropsWithChildren, useRef } from "react";

import { fadeTransition, surfaceTransition } from "../_shared/transitions";
import { useDragToDismiss } from "../hooks/useDragToDismiss";
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import { SlideOverContext, type SlideOverSide } from "./SlideOverContext";
import * as styles from "./SlideOver.module.scss";

export type SlideOverRootProps = PropsWithChildren<{
  side: SlideOverSide;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dismissible?: boolean;
  isElastic?: boolean;
  isDirectionLocked?: boolean;
}>;

const offscreen = { left: "-100%", right: "100%" } as const;

const Portal = ({ children }: PropsWithChildren) => (
  <DialogPrimitive.Portal forceMount>{children}</DialogPrimitive.Portal>
);

const Backdrop = ({ className }: { className?: string }) => {
  const { open } = useContext(SlideOverContext);

  return (
    <AnimatePresence>
      {open && (
        <DialogPrimitive.Overlay forceMount asChild>
          <motion.div
            className={cx(styles.backdrop, className)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fadeTransition}
          />
        </DialogPrimitive.Overlay>
      )}
    </AnimatePresence>
  );
};

const Panel = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const { open, setOpen, side, dismissible, isElastic, isDirectionLocked } =
    useContext(SlideOverContext);
  const hasCoarsePointer = useMediaQuery("(pointer: coarse)");
  const isDesktopWidth = useMediaQuery(DESKTOP_MEDIA_QUERY);

  const blockDismiss = dismissible
    ? undefined
    : (event: Event) => event.preventDefault();

  const { dragProps } = useDragToDismiss({
    edge: side,
    enabled: dismissible && (hasCoarsePointer || !isDesktopWidth),
    onDismiss: () => setOpen(false),
    isElastic,
    isDirectionLocked,
  });

  return (
    <AnimatePresence>
      {open && (
        <DialogPrimitive.Content
          forceMount
          asChild
          aria-describedby={undefined}
          onEscapeKeyDown={blockDismiss}
          onPointerDownOutside={blockDismiss}
          onInteractOutside={blockDismiss}
          onOpenAutoFocus={(event) => {
            // prevent auto focus on first element
            event.preventDefault();
            // focus the panel instead
            panelRef.current?.focus();
          }}
        >
          <motion.div
            ref={panelRef}
            className={cx(styles.panel, styles[side], className)}
            initial={{ x: offscreen[side] }}
            animate={{ x: 0 }}
            exit={{ x: offscreen[side] }}
            transition={surfaceTransition}
            {...dragProps}
          >
            {children}
          </motion.div>
        </DialogPrimitive.Content>
      )}
    </AnimatePresence>
  );
};

type SlideOverTitleProps = PropsWithChildren<{
  visuallyHidden?: boolean;
}>;

const Title = ({ children, visuallyHidden }: SlideOverTitleProps) => {
  const title = <DialogPrimitive.Title>{children}</DialogPrimitive.Title>;

  return visuallyHidden ? (
    <VisuallyHidden asChild>{title}</VisuallyHidden>
  ) : (
    title
  );
};

const Close = (props: DialogPrimitive.DialogCloseProps) => {
  return <DialogPrimitive.Close asChild {...props} />;
};

const Root = ({
  children,
  side,
  open,
  onOpenChange,
  dismissible = true,
  isElastic = true,
  isDirectionLocked = false,
}: SlideOverRootProps) => (
  <SlideOverContext
    value={{
      open,
      setOpen: onOpenChange,
      side,
      dismissible,
      isElastic,
      isDirectionLocked,
    }}
  >
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  </SlideOverContext>
);

export const SlideOver = {
  Root,
  Portal,
  Backdrop,
  Panel,
  Title,
  Close,
};
